import { Pencil, X } from 'lucide-react';
import { useState } from 'react';

import { useGetParentByStudentId } from '~entities/parent';
import { useIsStudioV2 } from '~entities/studios/hooks/use-is-studio-v2';

import { EditParentForm } from '~/features/contacts/parent/parent-form';

import { Button } from '~/shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '~/shared/shadcn/ui/card';
import { Label } from '~/shared/shadcn/ui/label';

export const Contact = ({ studentId }: { studentId: string }) => {
  const [isEditing, setIsEditing] = useState(false);
  const { isV2 } = useIsStudioV2();

  const { data: parent, isLoading } = useGetParentByStudentId(studentId);
  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!parent) {
    return <div>Parent not found</div>;
  }

  if (isEditing) {
    return (
      <Card className='max-w-2xl'>
        <CardHeader className='flex flex-row items-center justify-between'>
          <CardTitle>Edit Contact</CardTitle>
          {!isV2 ? (
            <Button
              variant={'ghost'}
              onClick={() => {
                setIsEditing(false);
              }}
            >
              <X className='text-secondary' />
            </Button>
          ) : null}
        </CardHeader>
        <CardContent>
          <div>
            <EditParentForm
              studentId={studentId}
              parent={parent}
              onCancel={() => {
                setIsEditing(false);
              }}
            />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className='max-w-2xl'>
      <CardHeader className='flex flex-row items-center justify-between'>
        <CardTitle className='text-lg font-semibold'>Contact Details</CardTitle>
        {/* V1: pencil in header. V2 (ENR-4054): match Student Details bottom Edit. */}
        {!isV2 ? (
          <Button
            variant={'ghost'}
            onClick={() => {
              setIsEditing(true);
            }}
          >
            <Pencil className='text-secondary' />
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className='space-y-6'>
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
          <div>
            <Label className='text-sm font-medium text-muted-foreground'>
              First Name
            </Label>
            <p className='text-lg font-medium'>{parent.firstName || '-'}</p>
          </div>
          <div>
            <Label className='text-sm font-medium text-muted-foreground'>
              Last Name
            </Label>
            <p className='text-lg font-medium'>{parent.lastName || '-'}</p>
          </div>
        </div>

        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
          <div>
            <Label className='text-sm font-medium text-muted-foreground'>
              Family Name
            </Label>
            <p className='text-lg font-medium'>{parent.familyName || '-'}</p>
          </div>
          <div>
            <Label className='text-sm font-medium text-muted-foreground'>
              Email
            </Label>
            <p className='text-lg font-medium'>{parent.email || '-'}</p>
          </div>
        </div>

        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
          <div>
            <Label className='text-sm font-medium text-muted-foreground'>
              Mobile
            </Label>
            <p className='text-lg font-medium'>{parent.primaryPhone || '-'}</p>
          </div>
          <div>
            <Label className='text-sm font-medium text-muted-foreground'>
              Relation
            </Label>
            <p className='text-lg font-medium'>{parent.relation ?? '-'}</p>
          </div>
        </div>

        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
          <div>
            <Label className='text-sm font-medium text-muted-foreground'>
              Address
            </Label>
            <div className='mt-1 space-y-2'>
              <p className='text-lg font-medium'>
                {parent.address.street || '-'}
              </p>
              <p className='text-lg font-medium'>
                {parent.address.city || '-'}, {parent.address.state || '-'}{' '}
                {parent.address.zip || '-'}
              </p>
              <p className='text-lg font-medium'>
                {parent.address.country || '-'}
              </p>
            </div>
          </div>
        </div>

        {isV2 ? (
          <div className='flex flex-wrap justify-end gap-3'>
            <Button
              type='button'
              variant='secondary'
              onClick={() => {
                setIsEditing(true);
              }}
            >
              Edit
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};
